import { Chart as ChartJS, ChartDataset, ChartOptions } from 'chart.js';
import { TooltipItem } from 'chart.js/auto';
import { AuditLog } from '../../../../../../../app/ApiGen';
import { basePalette } from '../../../../../../../design-system';
import { updateChartZoomLocations } from '../../../../../../../utils/chart';
import { AuditLogModel, DataPoint } from './AuditLogModel';
import { CHART_DATE_FORMAT, diagnosticsChartIds, DiagnosticsChartType } from './meteringCharts';

type AmberChartDataset = ChartDataset<'bar' | 'line' | 'scatter', DataPoint[]>;

type AuditLogKey =
  | Extract<
      keyof AuditLogModel,
      'buyPrice' | 'chargeBelowThreshold' | 'doNotChargeAboveThreshold' | 'error' | 'sellPrice'
    >
  | 'charge'
  | 'doNotCharge'
  | 'solar';

export type TooltipContext = Omit<TooltipItem<'bar' | 'line'>, 'parsed'> & { parsed: DataPoint };

const { borderColor: defaultBorderColour } = ChartJS.defaults;

function isDataPoint(value: unknown): value is Required<DataPoint> {
  return !!value && typeof value === 'object' && 'x' in value && 'y' in value;
}

function isAuditLogError(value: unknown): value is Required<AuditLogModel['error']> {
  return typeof value === 'object' && !!value && 'name' in value && 'message' in value;
}

const AmberColour = {
  Charge: '#c5f9d7',
  DoNotCharge: '#bbbbbb',
  Solar: '#FFFFAA',
  SellPrice: '#FFC107',
  BuyPrice: '#1976d2',
  ChargeBelow: '#006400',
  DoNotChargeAbove: '#404040',
  Error: basePalette.red.light,
};

const barChartSettings: Omit<ChartDataset<'bar'>, 'data'> = {
  barThickness: 'flex',
  type: 'bar',
};

const lineChartSettings: Omit<ChartDataset<'line'>, 'data'> = {
  backgroundColor: 'white',
  spanGaps: true,
  stack: 'combined',
  type: 'line',
};

const stackOrder = { error: 1, threshold: 2, sellPrice: 3, buyPrice: 4, behaviour: 5 };

const thresholdChartSettings = { borderDash: [5, 5], borderWidth: 2, order: stackOrder.threshold, pointStyle: 'dash' };

export const AmberChartSettings = {
  buyPrice: {
    ...barChartSettings,
    label: 'Buy Price',
    backgroundColor: AmberColour.BuyPrice,
    borderColor: AmberColour.BuyPrice,
    order: stackOrder.buyPrice,
  } as ChartDataset<'bar'>,
  charge: {
    ...barChartSettings,
    label: 'Charge',
    borderColor: AmberColour.Charge,
    backgroundColor: AmberColour.Charge,
    order: stackOrder.behaviour,
  } as ChartDataset<'bar'>,
  chargeBelowThreshold: {
    ...lineChartSettings,
    ...thresholdChartSettings,
    label: 'Charge Below',
    borderColor: AmberColour.ChargeBelow,
  } as ChartDataset<'line'>,
  doNotCharge: {
    ...barChartSettings,
    label: `Don't Charge`,
    borderColor: AmberColour.DoNotCharge,
    backgroundColor: AmberColour.DoNotCharge,
    order: stackOrder.behaviour,
  } as ChartDataset<'bar'>,
  doNotChargeAboveThreshold: {
    ...lineChartSettings,
    ...thresholdChartSettings,
    label: `Don't Charge Above`,
    borderColor: AmberColour.DoNotChargeAbove,
  } as ChartDataset<'line'>,
  error: {
    type: 'scatter',
    label: 'Error',
    borderColor: AmberColour.Error,
    borderWidth: 4,
    pointStyle: 'circle',
    order: stackOrder.error,
  } as ChartDataset<'scatter'>,
  sellPrice: {
    ...barChartSettings,
    label: 'Sell Price',
    borderColor: AmberColour.SellPrice,
    backgroundColor: AmberColour.SellPrice,
    order: stackOrder.sellPrice,
  } as ChartDataset<'bar'>,
  solar: {
    ...barChartSettings,
    label: 'Solar',
    borderColor: AmberColour.Solar,
    backgroundColor: AmberColour.Solar,
    order: stackOrder.behaviour,
  } as ChartDataset<'bar'>,
} as const;

export class AmberChartModel {
  private readonly meterValuesLogs: AuditLogModel[];

  private readonly dateRange: { start: Date; end: Date };

  constructor({ auditLogs, startDate, endDate }: { auditLogs: AuditLog[]; startDate: Date; endDate: Date }) {
    this.meterValuesLogs = auditLogs.flatMap<AuditLogModel>((auditLog) => {
      const auditLogModel = new AuditLogModel(auditLog);
      if (!auditLogModel.isFromMeterValuesUpdate()) {
        return [];
      }
      return [auditLogModel];
    });
    this.dateRange = { start: startDate, end: endDate };
  }

  private getBehaviourYValue(): number {
    const maximumValue = this.meterValuesLogs.reduce<number>((currentMaximum, auditLogModel) => {
      const { maximumNumericalValue } = auditLogModel;
      if (maximumNumericalValue > currentMaximum) {
        return maximumNumericalValue;
      }
      return currentMaximum;
    }, 0);

    return maximumValue * 1.25;
  }

  chartOptions(): ChartOptions<'bar' | 'line'> {
    return {
      animation: false,
      interaction: { intersect: false, mode: 'index' },
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: { callbacks: { label: this.tooltip } },
        zoom: {
          pan: { enabled: true, mode: 'x', modifierKey: 'ctrl' },
          zoom: {
            drag: { enabled: true },
            mode: 'x',
            onZoomComplete: (selectedChart) =>
              updateChartZoomLocations<DiagnosticsChartType>(selectedChart, diagnosticsChartIds),
          },
        },
      },
      responsive: true,
      scales: {
        x: {
          max: this.dateRange.end.toISOString(),
          min: this.dateRange.start.toISOString(),
          stacked: true,
          time: { tooltipFormat: CHART_DATE_FORMAT },
          type: 'time',
        },
        y: {
          afterFit(scale) {
            // eslint-disable-next-line no-param-reassign -- -- Reassigning the `scale` param's properties is the accepted way to set dimensions in ChartJS
            scale.width = 100;
          },
          afterSetDimensions: (scale) => {
            // eslint-disable-next-line no-param-reassign -- Reassigning the `scale` param's properties is the accepted way to set dimensions in ChartJS
            scale.maxWidth = 100;
          },
          grid: {
            color: ({ tick }) => {
              if (tick.value !== 0 && typeof defaultBorderColour === 'string') {
                return defaultBorderColour;
              }
              return 'black';
            },
            z: 1,
          },
          stacked: false,
          ticks: { crossAlign: 'near' },
          title: { display: true, text: 'Amber Price (c)' },
        },
      },
    };
  }

  datasets(): AmberChartDataset[] {
    const behaviourYValue = this.getBehaviourYValue();

    const datasets = this.meterValuesLogs.reduce<Record<AuditLogKey, AmberChartDataset['data']>>(
      (acc, auditLogModel) => ({
        ...acc,
        buyPrice: [...acc.buyPrice, auditLogModel.getDataPoint({ key: 'buyPrice' })],
        charge: [
          ...acc.charge,
          auditLogModel.getBehaviourDataPoint({
            behaviour: 'Charge',
            value: behaviourYValue,
          }),
        ],
        chargeBelowThreshold: [
          ...acc.chargeBelowThreshold,
          auditLogModel.getDataPoint({ key: 'chargeBelowThreshold' }),
        ],
        doNotCharge: [
          ...acc.doNotCharge,
          auditLogModel.getBehaviourDataPoint({ behaviour: 'DoNotCharge', value: behaviourYValue }),
        ],
        doNotChargeAboveThreshold: [
          ...acc.doNotChargeAboveThreshold,
          auditLogModel.getDataPoint({ key: 'doNotChargeAboveThreshold' }),
        ],
        error: [...acc.error, { ...auditLogModel.getErrorDataPoint(), error: auditLogModel.error }],
        sellPrice: [...acc.sellPrice, auditLogModel.getDataPoint({ key: 'sellPrice' })],
        solar: [...acc.solar, auditLogModel.getBehaviourDataPoint({ behaviour: 'Solar', value: behaviourYValue })],
      }),
      {
        buyPrice: [],
        charge: [],
        chargeBelowThreshold: [],
        doNotCharge: [],
        doNotChargeAboveThreshold: [],
        error: [],
        sellPrice: [],
        solar: [],
      },
    );

    return [
      { ...AmberChartSettings.buyPrice, data: datasets.buyPrice },
      { ...AmberChartSettings.charge, data: datasets.charge },
      { ...AmberChartSettings.chargeBelowThreshold, data: datasets.chargeBelowThreshold },
      { ...AmberChartSettings.doNotCharge, data: datasets.doNotCharge },
      { ...AmberChartSettings.doNotChargeAboveThreshold, data: datasets.doNotChargeAboveThreshold },
      { ...AmberChartSettings.error, data: datasets.error },
      { ...AmberChartSettings.sellPrice, data: datasets.sellPrice },
      { ...AmberChartSettings.solar, data: datasets.solar },
    ];
  }

  tooltip = (context: TooltipContext): string => {
    const label = context.dataset.label ?? '';
    const value = context.parsed.y;
    if (value === null) {
      return '';
    }
    if (
      label === AmberChartSettings.solar.label ||
      label === AmberChartSettings.charge.label ||
      label === AmberChartSettings.doNotCharge.label
    ) {
      return `Behaviour: ${label}`;
    }
    if (label === 'Error') {
      return isDataPoint(context.raw) && isAuditLogError(context.raw.error)
        ? `Error: ${context.raw.error.message}`
        : 'An error occurred';
    }
    return `${label}: ${value.toFixed(2)}c`;
  };
}
